import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// Une seule règle de pagination pour toutes les listes de l'application.
// La page courante vit dans l'adresse (`?page=`), ce qui permet le retour
// arrière du navigateur et le partage d'un lien. La taille (`?taille=`) aussi.

export const TAILLES_PAGE = [20, 50, 100];
export const TAILLE_PAGE_PAR_DEFAUT = 20;

const entierPositif = (valeur) => {
  const n = Number.parseInt(valeur, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Découpe une liste: pure, testable sans écran. */
export function paginer(items, page, taille) {
  const liste = Array.isArray(items) ? items : [];
  const total = liste.length;
  const parPage = TAILLES_PAGE.includes(taille) ? taille : TAILLE_PAGE_PAR_DEFAUT;
  const nombrePages = Math.max(1, Math.ceil(total / parPage));
  const courante = Math.min(Math.max(1, entierPositif(page) || 1), nombrePages);
  const debut = (courante - 1) * parPage;
  const lignes = liste.slice(debut, debut + parPage);
  return {
    lignes,
    total,
    taille: parPage,
    page: courante,
    nombrePages,
    premiere: total === 0 ? 0 : debut + 1,
    derniere: debut + lignes.length,
  };
}

function usePageUrl({ reinitialiser, cle, taillePrefere }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const nomPage = cle ? `${cle}-page` : 'page';
  const nomTaille = cle ? `${cle}-taille` : 'taille';

  const tailleUrl = entierPositif(searchParams.get(nomTaille));
  const taille = TAILLES_PAGE.includes(tailleUrl)
    ? tailleUrl
    : TAILLES_PAGE.includes(taillePrefere)
      ? taillePrefere
      : TAILLE_PAGE_PAR_DEFAUT;
  const pageUrl = searchParams.get(nomPage);

  const modifier = useCallback(
    (changer, remplacer) => {
      setSearchParams(
        (precedent) => {
          const suivant = new URLSearchParams(precedent);
          changer(suivant);
          return suivant;
        },
        { replace: remplacer },
      );
    },
    [setSearchParams],
  );

  const allerA = useCallback(
    (numero) => {
      const n = Math.max(1, entierPositif(numero) || 1);
      modifier((p) => {
        if (n === 1) p.delete(nomPage);
        else p.set(nomPage, String(n));
      }, false);
    },
    [modifier, nomPage],
  );

  const changerTaille = useCallback(
    (nouvelle) => {
      modifier((p) => {
        p.delete(nomPage);
        if (Number(nouvelle) === TAILLE_PAGE_PAR_DEFAUT) p.delete(nomTaille);
        else p.set(nomTaille, String(nouvelle));
      }, false);
    },
    [modifier, nomPage, nomTaille],
  );

  // Une recherche ou un filtre ramène à la page 1 (sans ajouter d'étape à
  // l'historique). Le premier rendu ne touche à rien: on garde `?page=` reçu.
  const precedente = useRef(reinitialiser);
  useEffect(() => {
    if (precedente.current === reinitialiser) return;
    precedente.current = reinitialiser;
    modifier((p) => p.delete(nomPage), true);
  }, [reinitialiser, modifier, nomPage]);

  return { pageUrl, taille, allerA, changerTaille };
}

/**
 * Pagination côté client d'une liste déjà chargée.
 *
 * - `reinitialiser`: texte qui résume les recherches et filtres; quand il
 *   change, on revient à la page 1.
 * - `cle`: préfixe des paramètres d'adresse quand un écran porte plusieurs
 *   listes (`cle: 'camions'` donne `?camions-page=`).
 */
export function usePagination(items, { reinitialiser = '', cle = '', taillePrefere } = {}) {
  const { pageUrl, taille, allerA, changerTaille } = usePageUrl({ reinitialiser, cle, taillePrefere });
  const resultat = useMemo(() => paginer(items, pageUrl, taille), [items, pageUrl, taille]);
  return { ...resultat, allerA, changerTaille };
}

/**
 * Pagination côté serveur: l'écran demande `skip`/`limit` à l'API et fournit
 * le `total` qu'elle renvoie. Tant que le total est inconnu (premier
 * chargement), la page de l'adresse est demandée telle quelle.
 */
export function usePaginationServeur(total, { reinitialiser = '', cle = '', taillePrefere } = {}) {
  const { pageUrl, taille, allerA, changerTaille } = usePageUrl({ reinitialiser, cle, taillePrefere });
  const nombre = Number(total) || 0;
  const nombrePages = Math.max(1, Math.ceil(nombre / taille));
  const demandee = entierPositif(pageUrl) || 1;
  const page = nombre > 0 ? Math.min(demandee, nombrePages) : demandee;
  const debut = (page - 1) * taille;
  return {
    total: nombre,
    taille,
    page,
    nombrePages,
    premiere: nombre === 0 ? 0 : debut + 1,
    derniere: Math.min(nombre, debut + taille),
    skip: debut,
    limit: taille,
    allerA,
    changerTaille,
  };
}

export default usePagination;
